import { ReportDraftKernel } from "./ReportDraftKernel";
import { reportDraftDb } from "./reportDraftDb";
import { TurnKernel } from "../assistant-offline/turnKernel";
import { setDrainTrigger } from "../assistant-offline/turnEvents";
import { cancelDrainRetry, scheduleDrainRetry } from "../assistant-offline/retrySchedule";

// Sincronização global de rascunhos offline (BEL-1578). A app é server-rendered,
// logo cada navegação é um page load — corremos um syncAll best-effort em CADA
// carregamento e ao voltar a rede, para o rascunho subir ao servidor assim que o
// utilizador navega por qualquer parte da app (não só ao abrir o reporte).
// Fila vazia = no-op (apenas uma leitura ao IndexedDB).
//
// window.belDraftSync é exposto para botões e scripts inline do Blade, que não
// conseguem importar ES modules.
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || null;

// BEL-1549 M3: relaya o token CSRF para o IndexedDB para o Service Worker o ler
// quando drenar a fila em background (o SW não tem acesso ao document).
if (csrfToken) {
	reportDraftDb.putKv("assistant_csrf", csrfToken).catch(() => {});
}

const kernel = new ReportDraftKernel({
	csrfToken,
});

// Drena os turnos offline do ASSISTENTE a partir de QUALQUER página (ver
// syncAssistantTurns). apiBaseFor espelha o ensureTurnKernel do taskMixin.
const assistantTurnKernel = new TurnKernel({
	apiBaseFor: visitId => `/admin/currentVisit/${visitId}/assistant`,
	csrfToken,
});

async function syncNow() {
	if (!navigator.onLine) {
		return kernel.pendingCount();
	}
	await kernel.syncAll();
	const pending = await kernel.pendingCount();
	document.dispatchEvent(new CustomEvent("report-draft:synced", { detail: { pending } }));
	return pending;
}

// Drena os turnos offline do Assistente (fotos → S3, depois POST do turno) a
// partir de QUALQUER página da app. O ReportDraftKernel.syncAll filtra as ops
// kind:'assistant', por isso antes só o TurnKernel da página do Assistente as
// drenava — se o utilizador recuperasse a rede sem reabrir o Assistente, as
// fotos ficavam presas no IndexedDB. O servidor é idempotente por
// (visit_id, client_uuid), logo um drain concorrente com a página do Assistente
// é seguro (devolve replay). Best-effort e só online.
/**
 * Conta as ops do assistente ainda por drenar.
 *
 * NÃO usar o valor devolvido por `syncAll`: o `drainAssistantQueue` filtra por
 * `kind === "assistant"` para processar, mas devolve `allQueue().length` — a
 * fila INTEIRA, incluindo os rascunhos manuais que este drain nunca toca nem
 * remove. Armar a escada com esse número deixá-la-ia permanentemente armada por
 * trabalho que não é nosso, a gastar as seis tentativas em vão e a morrer antes
 * de haver uma falha real do assistente (BEL-1751).
 */
async function pendingAssistantOps() {
	try {
		return (await reportDraftDb.allQueue()).filter(op => op.kind === "assistant").length;
	} catch (e) {
		return 0;
	}
}

async function syncAssistantTurns() {
	if (!navigator.onLine) {
		// Offline não é falha retentável: quando a rede voltar há evento para isso
		// (online / connection change). Manter a escada armada aqui só gastaria
		// bateria a tentar contra um rádio desligado.
		cancelDrainRetry();

		return;
	}
	try {
		await assistantTurnKernel.syncAll();
		if ((await pendingAssistantOps()) > 0) {
			// Ficou trabalho do assistente por fazer com o dispositivo online —
			// 429/408 do servidor, ou um PUT ao S3 que estalou. A rede não mudou,
			// logo nenhum evento voltará a disparar: a falha agenda a própria
			// tentativa.
			scheduleDrainRetry(retryDrain);
		} else {
			cancelDrainRetry();
		}
	} catch (e) {
		scheduleDrainRetry(retryDrain);
	}
}

/**
 * Callback da escada. Chama SÓ o drain do assistente — não `autoRun`, que
 * arrastaria consigo o sync dos rascunhos manuais e o varrimento de visitas
 * fechadas a cada degrau, trabalho que nada tem a ver com a falha que armou a
 * escada.
 */
function retryDrain() {
	setDrainTrigger("retry");
	syncAssistantTurns();
}

// Tipos de rascunho manual com chave localStorage `${visitId}progress_${type}`.
const DRAFT_TYPES = ["linear", "attendance", "competitorshopping"];

// Trabalho desta visita que ainda não chegou ao servidor: ops na fila OU turnos
// do assistente por confirmar. Fail-closed — IndexedDB ilegível conta como
// "tem pendentes", porque purgar às cegas apaga fotos que ninguém mais tem.
async function hasUnsyncedWork(visitId) {
	const v = String(visitId);
	try {
		const queue = await reportDraftDb.allQueue();
		if (queue.some(op => op && String(op.visitId) === v)) {
			return true;
		}
		const turns = await reportDraftDb.allTurns();
		return turns.some(t => t && String(t.visitId) === v && t.syncState !== "confirmed");
	} catch (e) {
		return true;
	}
}

// Limpa todos os rascunhos de uma visita (localStorage dos 3 tipos + IndexedDB:
// drafts/photos/sync_queue) — usado ao encerrar a visita, para não deixar sujeira
// nem permitir que um depósito offline pendente recrie a linha no servidor.
//
// NUNCA purga uma visita com trabalho por sincronizar (BEL-1751). O fecho da
// visita chamava isto incondicionalmente, e um turno à espera de retentativa
// (409 de lote incompleto, 5xx, sem rede) era apagado com as fotos locais — a
// foto já subida ficava órfã no S3 e o GPV via a visita sem ela. Aqui a fila é
// soberana: a sujeira fica para o varrimento seguinte, quando já drenou.
//
// @returns {Promise<boolean>} true se purgou; false se havia pendentes.
async function purgeVisit(visitId) {
	if (await hasUnsyncedWork(visitId)) {
		return false;
	}
	DRAFT_TYPES.forEach(type => localStorage.removeItem(`${visitId}progress_${type}`));
	localStorage.removeItem("progress_linear"); // legacy (sem prefixo de visita)
	localStorage.removeItem("bkp_linear"); // backup do form linear
	await kernel.purgeVisit(visitId);

	return true;
}

// Limpa o rascunho de UM relatório: localStorage do form manual + IndexedDB +
// qualquer depósito offline pendente. Usado quando o relatório é submetido pela
// Conferência da IA (tab Relatórios), porque esse caminho de submit NÃO passa
// pelo form manual que normalmente faz esta limpeza. Sem isto, o cartão
// "Terminar Visita" continuava a assinalar "rascunho por submeter" para algo já
// gravado, e o draft ficava no localStorage/IndexedDB.
async function purgeReport(visitId, reportType) {
	const type = String(reportType || "").toLowerCase();

	if (!visitId || !type) {
		return;
	}

	localStorage.removeItem(`${visitId}progress_${type}`);

	if (type === "linear") {
		localStorage.removeItem("progress_linear"); // legacy (sem prefixo)
		localStorage.removeItem("bkp_linear"); // backup do form linear
	}

	try {
		await kernel.purge(visitId, type); // IndexedDB: deleteDraft(`${visitId}:${type}`)
		await reportDraftDb.dequeueDraftDeposit(visitId, type);
	} catch (e) {
		// limpeza best-effort — nunca propaga
	}
}

// Varre o IndexedDB à procura de visitas já FECHADAS no servidor e limpa a
// respetiva sujeira (rascunhos/fotos/turnos offline) — apanha dados deixados
// por visitas encerradas offline, noutro dispositivo, ou antes do purge por
// visita existir. Best-effort e só online; sem rede é no-op (corre de novo no
// próximo `online`/page load). Conservador: o servidor só devolve visitas do
// próprio utilizador já finalizadas, por isso uma visita em progresso (incl. a
// atual) nunca é apagada.
async function sweepClosedVisits() {
	if (!navigator.onLine) {
		return 0;
	}
	let visitIds = [];
	try {
		visitIds = await reportDraftDb.allVisitIds();
	} catch (e) {
		return 0;
	}
	if (!visitIds.length) {
		return 0;
	}
	let completed = [];
	try {
		const res = await fetch("/admin/assistant/offline/completed-visits", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),
			},
			body: JSON.stringify({ visit_ids: visitIds }),
		});
		if (!res.ok) {
			return 0;
		}
		const data = await res.json();
		completed = Array.isArray(data.completed) ? data.completed : [];
	} catch (e) {
		return 0;
	}
	// Nunca purgar uma visita com ops ainda na fila local — purgeVisit apagaria
	// uploads pendentes (o servidor pode considerá-la purgável antes de a fila
	// deste dispositivo drenar).
	let busy = new Set();
	try {
		busy = new Set((await reportDraftDb.allQueue()).map(op => String(op.visitId)));
	} catch (e) {
		// Fila ilegível = estado indeterminado — abortar o varrimento (fail-closed):
		// purgar sem saber o que está pendente apagaria uploads por enviar. O
		// próximo ciclo (5 min) tenta de novo.
		return 0;
	}
	let purged = 0;
	for (const visitId of completed) {
		if (busy.has(String(visitId))) {
			continue;
		}
		try {
			if (await purgeVisit(visitId)) {
				purged++;
			}
		} catch (e) {
			// limpeza best-effort — nunca propaga
		}
	}
	return purged;
}

// Faxina LOCAL por inatividade: ao contrário do sweepClosedVisits (que espelha o
// servidor e mantém a janela de conferência de 7 dias), isto liberta o storage do
// dispositivo apagando o cache de visitas que NÃO são tocadas há mais de 24h. É
// puramente local — corre mesmo offline e não toca no TTL do servidor. Reabrir a
// visita online re-hidrata via GET /session.
//
// Anti-perda (mesma garantia do "busy guard" do sweep + estado dos turnos): nunca
// purga uma visita com ops ainda na sync_queue (rascunhos/fotos por enviar) nem
// com turnos do assistente em estado != "confirmed" (captura offline por
// sincronizar). Idade medida pelo MAIS RECENTE de cachedAt (assistant_session) e
// capturedAt (assistant_turns) — uma visita reaberta há pouco renova o cachedAt e
// escapa à purga. Fail-closed: leitura ilegível aborta sem apagar nada.
const LOCAL_STALE_TTL_MS = 24 * 60 * 60 * 1000;

async function purgeStaleLocal() {
	const cutoff = Date.now() - LOCAL_STALE_TTL_MS;
	let sessions = [];
	let turns = [];
	let queue = [];
	try {
		sessions = await reportDraftDb.allSessionSnapshots();
		turns = await reportDraftDb.allTurns();
		queue = await reportDraftDb.allQueue();
	} catch (e) {
		return 0;
	}

	// Visitas com ops por sincronizar — nunca purgar (apagaria envios pendentes).
	const busy = new Set(queue.map(op => String(op.visitId)));

	// Visitas com qualquer turno do assistente ainda não confirmado — idem.
	const unsafeTurns = new Set();
	for (const t of turns) {
		if (t && t.syncState !== "confirmed") {
			unsafeTurns.add(String(t.visitId));
		}
	}

	// Timestamp local mais recente por visita (cachedAt + capturedAt).
	const newestTs = new Map();
	const bump = (visitId, iso) => {
		if (!iso) {
			return;
		}
		const ts = Date.parse(iso);
		if (!Number.isFinite(ts)) {
			return;
		}
		const v = String(visitId);
		if (ts > (newestTs.get(v) || 0)) {
			newestTs.set(v, ts);
		}
	};
	for (const s of sessions) {
		bump(s.visitId, s.cachedAt);
	}
	for (const t of turns) {
		bump(t.visitId, t.capturedAt);
	}

	let purged = 0;
	for (const s of sessions) {
		const v = String(s.visitId);
		if (busy.has(v) || unsafeTurns.has(v)) {
			continue;
		}
		if ((newestTs.get(v) || 0) >= cutoff) {
			continue;
		}
		try {
			if (await purgeVisit(v)) {
				purged++;
			}
		} catch (e) {
			// faxina best-effort — nunca propaga
		}
	}
	return purged;
}

// Throttle do varrimento a 1×/5min por aba (sessionStorage) — o autoRun corre a
// cada navegação, e a sujeira de visitas fechadas não muda a esse ritmo.
const SWEEP_THROTTLE_KEY = "belDraftSweepAt";
const SWEEP_THROTTLE_MS = 5 * 60 * 1000;

function sweepIfDue() {
	let last = 0;
	try {
		last = parseInt(sessionStorage.getItem(SWEEP_THROTTLE_KEY) || "0", 10) || 0;
	} catch (e) {
		last = 0;
	}
	if (Date.now() - last < SWEEP_THROTTLE_MS) {
		return;
	}
	try {
		sessionStorage.setItem(SWEEP_THROTTLE_KEY, String(Date.now()));
	} catch (e) {
		// sessionStorage indisponível — segue sem throttle
	}
	sweepClosedVisits().catch(() => {});
	// Faxina local por inatividade (24h) — pura faxina de storage no dispositivo,
	// independente da rede e do TTL do servidor. Best-effort.
	purgeStaleLocal().catch(() => {});
}

// Gate de abertura de nova visita (Etapa 3 §9): nova visita só abre com a fila
// local VAZIA (rascunhos manuais + fotos + turnos do assistente, de qualquer
// visita). Drena na hora; se restarem ops (sem rede/5xx), devolve o nº pendente
// e o caller bloqueia a abertura com mensagem.
async function pendingOpsCount() {
	try {
		return (await reportDraftDb.allQueue()).length;
	} catch (e) {
		return 0;
	}
}

async function drainAll() {
	await syncNow().catch(() => {});
	await syncAssistantTurns();
	return pendingOpsCount();
}

window.belDraftSync = {
	syncNow,
	pendingCount: () => kernel.pendingCount(),
	isOnline: () => navigator.onLine,
	purgeVisit,
	purgeReport,
	sweepClosedVisits,
	purgeStaleLocal,
	syncAssistantTurns,
	pendingOpsCount,
	drainAll,
};

function autoRun(trigger = "load") {
	setDrainTrigger(trigger);
	syncNow().catch(() => {});
	syncAssistantTurns();
	sweepIfDue();
}

// Throttle dos gatilhos de alta frequência (focus/visibility disparam a cada
// troca de tab/app) — drena no máximo 1×/5s; load e online não passam por aqui.
let lastTriggeredAt = 0;
function autoRunThrottled(trigger = "foreground") {
	const now = Date.now();
	if (now - lastTriggeredAt < 5000) {
		return;
	}
	lastTriggeredAt = now;
	autoRun(trigger);
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", () => autoRun("load"));
} else {
	autoRun("load");
}
window.addEventListener("online", () => autoRun("online"));

// Mobile: voltar à app (troca de app/tab) raramente dispara 'online' — a rede
// já voltou ENQUANTO a tab estava em segundo plano. Sem estes gatilhos, os
// turnos offline do assistente só subiam na próxima navegação.
window.addEventListener("focus", () => autoRunThrottled("focus"));
document.addEventListener("visibilitychange", () => {
	if (!document.hidden) {
		autoRunThrottled("visibility");
	}
});

// Oscilação de rede (BEL-1751). O caso medido em produção: a GPV captura em 3g
// fraca, o turno fica na fila, e 35 s depois o dispositivo salta para 4g — sem
// nunca ficar offline, por isso o evento `online` NÃO dispara e nada drenava até
// um page load acidental. A Network Information API emite `change` em cada
// transição de tipo efetivo; é o sinal certo, entregue pela plataforma, sem
// sondagem. Passa pelo throttle de 5 s, que protege contra tempestades numa
// rede instável. Chromium/Android (a frota); ausente em Safari/iOS → no-op.
const netInfo =
	typeof navigator !== "undefined"
		? navigator.connection || navigator.mozConnection || navigator.webkitConnection || null
		: null;

const onNetChange = () => autoRunThrottled("connection-change");

if (netInfo && typeof netInfo.addEventListener === "function") {
	netInfo.addEventListener("change", onNetChange);
} else if (netInfo) {
	// WebViews e Samsung Internet mais antigos expõem o objeto mas só suportam
	// a propriedade `onchange`. Três linhas de seguro para uma frota Android
	// que não controlamos ao detalhe.
	netInfo.onchange = onNetChange;
}

// Background Sync (Android): o SW recebe o 'sync' quando a rede volta — mesmo
// com a tab em segundo plano — e faz postMessage aos clients. Drena AMBAS as
// filas (manual + assistente) em qualquer página aberta da app.
if ("serviceWorker" in navigator) {
	navigator.serviceWorker.addEventListener("message", e => {
		// "sw-message": o SW acordou e AVISOU a página, que é quem drena. Distinto
		// de "sw-sync", que só aparece quando o próprio SW drenou (app fechada).
		// A diferença é o que prova se o Background Sync funciona nestes tablets.
		if (e.data && e.data.type === "report-draft-sync") {
			autoRunThrottled("sw-message");
		}
	});
}

// Heartbeat: retry a cada 60s enquanto houver ops na fila (5xx/rede instável
// deixam ops para trás e, sem isto, nada reagendava o drain se o utilizador
// ficasse parado na mesma página). Fila vazia = uma leitura barata ao IndexedDB.
setInterval(() => {
	if (!navigator.onLine) {
		return;
	}
	reportDraftDb
		.allQueue()
		.then(queue => {
			if (queue.length > 0) {
				autoRun();
			}
		})
		.catch(() => {});
}, 60000);

// Forms server-rendered de início de visita: intercepta no capture phase, drena
// a fila e só então submete. data-bel-drain-ok evita loop no re-submit.
document.addEventListener(
	"submit",
	e => {
		const form = e.target;
		if (!form || !form.action || !/\/visit\/start(Planned|Unplanned)Visit\//.test(form.action)) {
			return;
		}
		if (form.dataset.belVueStart) {
			// Form gerido por Vue (página da loja): o handler @submit trata da
			// seleção de visita virtual/geo/countdown e chama o gate de drain
			// explicitamente em proceedWithVisitSubmit — intercetar aqui matava
			// esse fluxo (capture-phase + stopImmediatePropagation).
			return;
		}
		if (form.dataset.belDrainOk) {
			// "1" = drenado e re-submetido; "pending" = drain em curso (evita
			// double-drain num segundo clique enquanto a fila esvazia).
			return;
		}
		e.preventDefault();
		e.stopImmediatePropagation();
		form.dataset.belDrainOk = "pending";
		drainAll().then(left => {
			if (left > 0) {
				delete form.dataset.belDrainOk;
				const msg =
					"Ainda há " + left + " itens da visita anterior por enviar. Mantém-te ligado à rede e tenta novamente.";
				if (typeof window.toastError === "function") {
					window.toastError(msg);
				} else {
					alert(msg);
				}
				return;
			}
			sweepClosedVisits().catch(() => {});
			form.dataset.belDrainOk = "1";
			form.submit();
		});
	},
	true
);
